import { ProductEntity, ProductTeamMember, ReportEntity, SprintEntity } from '@data/utils/types';
import { fullDate } from '@app/utils/helpers/date-utils';

// The two documents behind Panenco's Belgian BV O&O claim: the projects, and the timesheets evidencing
// them. Everything here is a pure function of what the reads already return, so the preview and the file
// are built from one code path and cannot disagree.

export type DocType = 'projects' | 'timesheets';

// The column headings the claim is filed with. Dutch and fixed by the filing, so never translated.
export const PROJECT_COLUMNS = [
  'Projectnaam',
  'Projectcode',
  'Beschrijving',
  'Academische samenwerking',
  'Startdatum',
  'Einddatum',
];
export const TIMESHEET_COLUMNS = [
  'Duur (uren)',
  'Rijksregisternummer',
  'Personeelsnummer',
  'Achternaam',
  'Voornaam',
  'Datum',
  'Taakomschrijving',
  'Project (code of naam)',
  'R&D %',
  'Type (WORK_TIME/ABSENCE)',
];

/**
 * Every exported row is worked time. Absence and holiday are non-billable products and are excluded with
 * the rest of them, so the column carries one value — and it is the one the heading itself declares. The
 * prototype wrote `WERK_TIJD`, which contradicts its own heading.
 */
const WORKED_TIME = 'WORK_TIME';

/** One sprint's reports on one product, as the export reads them. */
export interface SprintProductReports {
  sprint: SprintEntity;
  product: ProductEntity;
  reports: ReportEntity[];
}

export interface ExportPeople {
  /** Panenco's own reference for a person, by membership. Absent for anybody who has none recorded. */
  memberNumbers: Map<string, string>;
  /** The R&D share of a person's work on a product, keyed `productId:membershipId`. */
  rndShares: Map<string, number>;
}

/** A reports-join row carries a synthesised 100% nobody chose, so it is left out and its cell exports blank. */
export const rndSharesFor = (teams: { productId: string; team: ProductTeamMember[] }[]): Map<string, number> => {
  const shares = new Map<string, number>();
  teams.forEach(({ productId, team }) =>
    team.forEach(member => {
      if (member.joinedByTime) return;
      shares.set(`${productId}:${member.membershipId}`, member.rndShare);
    }),
  );

  return shares;
};

/** The first and last day of a `yyyy-MM` month, as day keys on the UTC calendar the rest of the app uses. */
export const monthBounds = (month: string): { first: string; last: string } => {
  const [year, monthNo] = month.split('-').map(Number);
  const last = new Date(Date.UTC(year, monthNo, 0));

  return { first: `${month}-01`, last: last.toISOString().slice(0, 10) };
};

/** Whether a sprint has any day inside the month — the sprints whose reports are worth reading. */
export const overlapsMonth = (sprint: SprintEntity, month: string): boolean => {
  const { first, last } = monthBounds(month);

  return fullDate(sprint.startDate) <= last && fullDate(sprint.endDate) >= first;
};

/**
 * The month bounds which rows appear, not the sprint. A sprint spanning a month boundary contributes only
 * the days that fall inside the month.
 */
const filedInMonth = (reports: ReportEntity[], month: string): ReportEntity[] => {
  const { first, last } = monthBounds(month);

  return reports.filter(r => {
    const day = fullDate(r.workday);

    return day >= first && day <= last;
  });
};

// e.g. 2026 + sprint 5 → "202605", appended to the product's code so a project row names the sprint it
// covers as well as the product.
const sprintSuffix = (sprint: SprintEntity): string =>
  `${fullDate(sprint.startDate).slice(0, 4)}${String(sprint.number).padStart(2, '0')}`;

const codeFor = (product: ProductEntity, sprint: SprintEntity): string =>
  product.projectCode ? `${product.projectCode}-${sprintSuffix(sprint)}` : '';

/**
 * The projects document: what was worked on, and over which sprint.
 *
 * A project appears when time was logged on it **inside the month**. The prototype included it whenever the
 * sprint held any time at all, which put projects in the wrong month whenever a sprint straddled one.
 */
export const projectRows = (groups: SprintProductReports[], month: string): string[][] =>
  groups
    .filter(g => filedInMonth(g.reports, month).length > 0)
    .map(({ sprint, product }) => [
      product.name ?? '',
      codeFor(product, sprint),
      product.description ?? '',
      // Phill records no academic collaboration and this task does not add it. The column ships empty.
      '',
      fullDate(sprint.startDate),
      fullDate(sprint.endDate),
    ]);

/**
 * The timesheets document: one row per report filed inside the month.
 *
 * Every lookup is defensive. A product with no project code, or a person with no member number or no R&D
 * share recorded, exports an empty cell rather than failing the whole document — the manager can fill a
 * blank in, but cannot recover a document that refused to build.
 */
export const timesheetRows = (groups: SprintProductReports[], month: string, people: ExportPeople): string[][] =>
  groups.flatMap(({ sprint, product, reports }) =>
    filedInMonth(reports, month).map(report => {
      const membership = report.involvement?.membership;
      const membershipId = membership?.id ?? '';
      const share = people.rndShares.get(`${product.id}:${membershipId}`);

      return [
        String(report.hours ?? (report.minutes ? report.minutes / 60 : '')),
        // Phill does not store the national registry number, by decision. The column is present and always
        // empty, and the manager completes it by hand.
        '',
        people.memberNumbers.get(membershipId) ?? '',
        membership?.user?.lastName ?? '',
        membership?.user?.firstName ?? '',
        fullDate(report.workday),
        report.today ?? '',
        codeFor(product, sprint) || (product.name ?? ''),
        share === undefined ? '' : String(share),
        WORKED_TIME,
      ];
    }),
  );

/** `projects_july_2026.xlsx` — the document and the month it covers, and nothing else. */
export const exportFilename = (doc: DocType, month: string): string => {
  if (!month) return `${doc}.xlsx`;
  const [year, monthNo] = month.split('-').map(Number);
  const name = new Intl.DateTimeFormat('en-IE', { month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, monthNo - 1, 1)))
    .toLowerCase();

  return `${doc}_${name}_${year}.xlsx`;
};

/** One cell of the workbook, in the shape `write-excel-file` takes. */
export type SheetCell = { value: string; fontWeight?: 'bold' } | null;

/**
 * The workbook's single sheet, built from the same arrays the preview rendered: the headings in bold on row
 * one, then the rows.
 *
 * Every value goes out as text, deliberately. A cell left as text is the value Phill filed, whereas handing
 * Excel a number or a date invites it to re-read `8` as a quantity to sum and `2026-08-06` in whatever the
 * local date format is, which is how an export starts disagreeing with the screen it came from. A cell Phill
 * holds nothing for is empty rather than an empty string.
 */
export const toSheetData = (columns: string[], rows: string[][]): SheetCell[][] => [
  columns.map(heading => ({ value: heading, fontWeight: 'bold' as const })),
  ...rows.map(row => row.map(value => (value === '' ? null : { value }))),
];
